// ===========================================================================
// FinancialSummary — resumen financiero de la ficha: desglose de costo +
// trayectoria temporal (costo acumulado + Global Indicator) con doble eje.
// ===========================================================================
import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Stack, LinearProgress, CircularProgress } from "@mui/material";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { getPatientTrajectory } from "../../services";
import { COST_CATEGORIES } from "../../common/constants";
import { formatCurrencyCents, formatCompactCurrency, formatDate } from "../../utils/format";

export default function FinancialSummary({ header, cipId }) {
  const [trajectory, setTrajectory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const data = await getPatientTrajectory(cipId);
        if (active) setTrajectory(Array.isArray(data) ? data : []);
      } catch {
        if (active) setTrajectory([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [cipId]);

  const total = Number(header?.totalCost) || 0;
  const chartData = trajectory.map((s) => ({
    date: formatDate(s.sourceUploadDate),
    cost: Number(s.totalCost) || 0,
    gi: Number(s.globalIndicator) || 0,
  }));

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, mb: 2.5 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
        Financial summary
      </Typography>

      <Box sx={{ display: "grid", gap: 3, gridTemplateColumns: { xs: "1fr", md: "1fr 1.4fr" } }}>
        {/* Desglose por categoría */}
        <Box>
          <Typography variant="caption" color="text.secondary">
            Cost breakdown
          </Typography>
          <Stack spacing={1.2} sx={{ mt: 1 }}>
            {COST_CATEGORIES.map((cat) => {
              const value = Number(header?.[cat.key]) || 0;
              const pct = total > 0 ? (value / total) * 100 : 0;
              return (
                <Box key={cat.key}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2">{cat.label}</Typography>
                    <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
                      {formatCurrencyCents(value)}
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={pct}
                    sx={{ height: 6, borderRadius: 3, bgcolor: "action.hover", "& .MuiLinearProgress-bar": { bgcolor: cat.color } }}
                  />
                </Box>
              );
            })}
          </Stack>
        </Box>

        {/* Trayectoria */}
        <Box>
          <Typography variant="caption" color="text.secondary">
            Cost trajectory (accumulated cost vs Global Indicator)
          </Typography>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 260 }}>
              <CircularProgress size={24} />
            </Box>
          ) : chartData.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
              No history available.
            </Typography>
          ) : (
            <Box sx={{ height: 280, mt: 1 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis
                    yAxisId="cost"
                    tickFormatter={(v) => formatCompactCurrency(v)}
                    tick={{ fontSize: 11 }}
                    width={60}
                  />
                  <YAxis yAxisId="gi" orientation="right" domain={[0, 7]} tick={{ fontSize: 11 }} width={28} />
                  <Tooltip
                    formatter={(value, name) =>
                      name === "Cost" ? formatCurrencyCents(value) : value
                    }
                  />
                  <Legend />
                  <Line yAxisId="cost" type="monotone" dataKey="cost" name="Cost" stroke="#1976d2" strokeWidth={2} dot={{ r: 3 }} />
                  <Line yAxisId="gi" type="monotone" dataKey="gi" name="Global Indicator" stroke="#c62828" strokeWidth={2} strokeDasharray="5 4" dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
}
